import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount } from '../../../utils/global';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
} from '../../../theme/harmonateStyles';

const getPropertyId = (property) =>
  property?.propertyId ?? property?.PROPERTY_ID ?? property?.property_id ?? null;

const getPropertyLabel = (property) => {
  const code = property?.propertyCode || property?.PROPERTY_CODE || '';
  const name = property?.propertyName || property?.PROPERTY_NAME || '';
  return code && name ? `${code} - ${name}` : name || code || '';
};

const getGlAccountCode = (account) =>
  String(account?.glCode ?? account?.gl_code ?? account?.glAccountNo ?? '').trim();

const getGlAccountLabel = (account) => {
  if (!account || typeof account === 'string') return account || '';
  if (account.glAccount) return account.glAccount;
  const code = getGlAccountCode(account);
  const desc = account.glDescription ?? account.gl_description ?? account.glAccountName ?? '';
  return desc ? `${code} - ${desc}` : code;
};

const getAccountType = (account) =>
  String(account?.accountType ?? account?.account_type ?? '').trim();

const assignedLedgersColumns = [
  {
    field: 'glAccountCode',
    headerName: 'GL Account Code',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'glAccountName',
    headerName: 'GL Account Name',
    flex: 2,
    minWidth: 180,
  },
  {
    field: 'accountType',
    headerName: 'Account Type',
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'transactionCount',
    headerName: 'Transactions',
    flex: 0.8,
    minWidth: 100,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'totalAmount',
    headerName: 'Total Amount',
    flex: 1,
    minWidth: 120,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (params) => formatAmount(params.value),
  },
];

const AssignedLedgersDrawer = ({ open, onClose, selectedProperty, dateFrom, dateTo, glAccounts = [] }) => {
  const { showError } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tenantUuid = localStorage.getItem('tenant_id');
  const token = localStorage.getItem('token');
  const fastApiBase = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/pms-transactions`;

  const { glDescriptionByCode, accountTypeByCode } = useMemo(() => {
    const labelMap = new Map();
    const typeMap = new Map();
    const propertyId = getPropertyId(selectedProperty);
    glAccounts.forEach((account) => {
      const code = getGlAccountCode(account);
      if (!code) return;
      const accountPropertyId =
        account?.propertyId ?? account?.property_id ?? account?.PROPERTY_ID ?? null;
      if (
        propertyId &&
        accountPropertyId &&
        String(accountPropertyId) !== String(propertyId)
      ) {
        return;
      }
      if (!labelMap.has(code)) {
        labelMap.set(code, getGlAccountLabel(account));
      }
      const accountType = getAccountType(account);
      if (accountType && !typeMap.has(code)) {
        typeMap.set(code, accountType);
      }
    });
    return { glDescriptionByCode: labelMap, accountTypeByCode: typeMap };
  }, [glAccounts, selectedProperty]);

  const fetchAssignedLedgers = useCallback(async () => {
    const propertyId = getPropertyId(selectedProperty);
    if (!token || !tenantUuid || !propertyId) {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        tenant_uuid: tenantUuid,
        property_id: String(propertyId),
      });
      if (dateFrom) params.append('transaction_date_from', dateFrom);
      if (dateTo) params.append('transaction_date_to', dateTo);

      const resp = await fetch(`${fastApiBase}/assigned-ledgers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to load assigned ledgers');
      }
      const data = await resp.json();
      const list = Array.isArray(data) ? data : [];
      setRows(
        list.map((row) => {
          const code = row.gl_code || '';
          const label = glDescriptionByCode.get(code) || code;
          const name =
            label && label !== code && label.startsWith(`${code} - `)
              ? label.slice(code.length + 3)
              : label !== code
                ? label
                : '';
          return {
            id: code,
            glAccountCode: code,
            glAccountName: name,
            accountType: accountTypeByCode.get(code) || '',
            transactionCount: row.transaction_count ?? 0,
            totalAmount: Number(row.total_amount) || 0,
          };
        })
      );
    } catch (err) {
      setRows([]);
      setError(err.message || 'Failed to load assigned ledgers');
      showError(err.message || 'Failed to load assigned ledgers');
    } finally {
      setLoading(false);
    }
  }, [accountTypeByCode, dateFrom, dateTo, fastApiBase, glDescriptionByCode, selectedProperty, tenantUuid, token, showError]);

  useEffect(() => {
    if (open) {
      fetchAssignedLedgers();
    } else {
      setRows([]);
      setError(null);
    }
  }, [open, fetchAssignedLedgers]);

  const grandTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0),
    [rows]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 820,
          maxWidth: '100%',
          bgcolor: ht.drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, color: ht.white }}>
          Assigned Ledgers
        </Typography>
        <IconButton onClick={onClose} sx={harmonateDialogCloseIconSx} aria-label="Close drawer">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Typography variant="body2" sx={{ mb: 0.5, color: ht.text }}>
          {selectedProperty ? getPropertyLabel(selectedProperty) : 'No property selected'}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress sx={{ color: ht.text }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box
            sx={{
              ...harmonateCardSx,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              height: 350,
              bgcolor: ht.white,
            }}
          >
            <DataGridPro
              rows={rows}
              columns={assignedLedgersColumns}
              columnResizeMode="onEnd"
              resizable
              sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
              getRowId={(row) => row.id}
              rowHeight={35}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <Typography sx={{ color: ht.text }}>No assigned ledgers found.</Typography>
                  </Box>
                ),
              }}
              pagination
              pageSize={100}
            />
          </Box>
        )}

        {!loading && !error && rows.length > 0 && (
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${ht.line}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: ht.text }}>
              Grand Total
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: ht.text }}>
              {formatAmount(grandTotal)}
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AssignedLedgersDrawer;
